import { FC } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import moment, { Moment } from 'moment-timezone';
import { useTheme } from '@mui/material';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { getTimezone } from '@mgm/common/utils';

type TCustomRangePickerField = {
  defaultValue?: [Moment, Moment];
  setDates: (value: [Moment, Moment]) => void;
  extraFooter?: React.ReactNode;
  readOnly?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  error?: boolean;
  format?: string
};

const CustomRangePicker: FC<TCustomRangePickerField> = ({
  defaultValue,
  setDates,
  readOnly = false,
  isRequired = false,
  disabled = false,
  format = 'DD/MM/YYYY',
}) => {
  const {
    control,
  } = useFormContext();

  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);

  const {
    field: {
      ref, value, onChange: onDateChange, ...inputProps
    },
  } = useController({
    name: 'dateRange',
    control,
    rules: {
      required: isRequired,
    },
    defaultValue: defaultValue || [null, null],
  });

  const handleDateChange = (dates: [Moment, Moment]) => {
    const timezone = getTimezone();

    const timezoneDates: [Moment, Moment] = [
      dates[0] ? moment.tz(dates[0], timezone) : moment.tz(timezone),
      dates[1] ? moment.tz(dates[1], timezone) : moment.tz(timezone),
    ];

    onDateChange(timezoneDates);
    setDates(timezoneDates);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DateRangePicker
        {...inputProps}
        value={value}
        onChange={handleDateChange as any}
        format={format}
        disabled={disabled || readOnly}
        calendars={2}
        sx={{
          '& .MuiPaper-root-MuiCard-root': {
            boxShadow: 'none',
          },
        }}
        slotProps={{
          textField: {
            size: 'small',
            variant: 'outlined',
            label: '',
            sx: {
              maxWidth: isMobile ? 150 : 120,
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export {
  CustomRangePicker,
};
